import { createServerFn } from "@tanstack/react-start";
import { whoami } from "@/lib/mma";
import { useAppSession } from "@/utils/session";
import { resolveClientIp } from "@/lib/utils.server";
import createLogger from "@/lib/logger.server";

const logger = createLogger("auth");

export const login = createServerFn({ method: "POST" })
  .inputValidator((data: { username: string; password: string }) => data)
  .handler(async ({ data }) => {
    const clientIp = resolveClientIp();
    const token = Buffer.from(`${data?.username}:${data?.password}`).toString("base64");
    const baseUrl = process.env.API_BASE_URL;
    let sessionObject = {};

    logger.info("Login attempt for %s from %s -> %s", data.username, clientIp, baseUrl);

    const response = await whoami({
      baseUrl,
      headers: {
        Authorization: `Basic ${token}`,
        Forwarded: `for=${clientIp}`,
      },
    });

    const passwordExpired = response.error?.errors?.auth && response.error?.errors?.auth?.type === "PASSWORD_EXPIRED";

    if (response.response.status !== 200) {
      logger.warn("Login failed for %s: HTTP %d", data.username, response.response.status);
      switch (response.response.status) {
        case 401:
          if (passwordExpired) {
            sessionObject = {
              username: data.username,
              displayName: undefined,
              passwordExpiredFlow: true,
              accessControl: {},
              profile: null,
              clientIp: clientIp,
              token,
              baseUrl,
            };
            break;
          }
          throw new Error("Invalid username or password");
        case 403:
          throw new Error("User is not active");
        case 404:
          throw new Error("User not found");
        case 500:
          throw new Error("Internal server error");
        default:
          throw new Error("An error occurred while logging in");
      }
    }

    logger.info(
      "Login successful for %s (profile: %s)",
      response.data?.data?.username,
      response.data?.data?.profile?.id
    );

    // Create a session
    const session = await useAppSession();

    if (!passwordExpired) {
      sessionObject = {
        username: response.data?.data?.username,
        displayName: response.data?.data?.displayName ?? undefined,
        passwordExpiredFlow: passwordExpired,
        accessControl: response.data?.data?.profile?.accessControlMatrix ?? {},
        profile: response.data?.data?.profile?.id?.toString() || "0",
        clientIp: clientIp,
        token,
        baseUrl,
      };
    }

    // Store the user's credentials and other information in the session
    await session.update(sessionObject);

    return {
      passwordExpiredFlow: passwordExpired,
      profile: !passwordExpired ? (sessionObject as any).profile : undefined,
      accessControl: !passwordExpired ? (sessionObject as any).accessControl : undefined,
    };
  });
